import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Multi-tenant rollout console (part 100).
 *
 * A release moves through three waves — canary, early, general — and stops at the first gate that
 * fails. This page creates a plan from three tenant lists, advances or resumes the next wave,
 * records a gate from measured traffic, and shows the simulated step log, the rollback plan of
 * each wave, and the wave that is currently halted.
 *
 * Nothing on this page deploys anything. Advancing records simulated step results; recording a
 * gate evaluates error rate, p95 and sample size against the wave thresholds. A failed gate halts
 * the rollout, keeps every completed wave, and a second advance resumes at the halted wave.
 *
 * The page is not routed from App.tsx in this part; the API mount is intentionally unmounted too.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type Cohort = 'canary' | 'early' | 'general'
type WaveStatus = 'pending' | 'awaiting_gate' | 'completed' | 'halted'
type PlanStatus = 'planned' | 'in_progress' | 'halted' | 'completed'
type NextAction = 'deploy' | 'resume' | 'await_gate' | 'done'

interface Thresholds {
  max_error_rate: number
  max_p95_ms: number
  min_requests: number
}

interface RolloutTenant {
  tenant_id: string
  slug: string
  name_th?: string
}

interface PlanStep {
  id: string
  position: number
  title_th: string
  detail_th: string
  estimated_seconds: number
  requires_approval: boolean
}

interface GateCheck {
  key: string
  label_th: string
  observed: string
  limit: string
  pass: boolean
  detail_th: string
  blocking: boolean
}

interface GateReport {
  decision: string
  decision_th: string
  passed: boolean
  summary_th: string
  checks: GateCheck[]
  requests: number
  errors: number
  error_rate: number
  p95_ms: number
  observed_at: string
  evaluated_at: string
  thresholds: Thresholds
}

interface Wave {
  id: string
  tenant_id: string
  plan_id: string
  position: number
  cohort: Cohort
  cohort_label_th: string
  label_th: string
  tenants: RolloutTenant[]
  tenant_count: number
  gate_thresholds: Thresholds
  deploy_plan: PlanStep[]
  rollback_plan: PlanStep[]
  status: WaveStatus
  status_label_th: string
  attempt: number
  last_gate?: GateReport
  failure_th?: string
  deployed_at?: string
  completed_at?: string
  halted_at?: string
}

interface RolloutPlan {
  id: string
  tenant_id: string
  plan_key: string
  name: string
  release: string
  status: PlanStatus
  status_label_th: string
  simulated: boolean
  waves_total: number
  tenants_total: number
  completed_waves: number
  created_by?: string
  created_by_role: string
  created_at: string
  updated_at: string
}

interface Step {
  id: string
  plan_id: string
  wave_id: string
  position: number
  attempt: number
  kind: 'deploy' | 'gate' | 'resume' | 'rollback'
  step_key: string
  title_th: string
  detail_th: string
  status: 'succeeded' | 'failed' | 'prepared' | 'skipped'
  simulated: boolean
  evidence_th: string
  metrics: Record<string, unknown>
  recorded_at: string
}

interface PlanDetail {
  plan: RolloutPlan
  waves: Wave[]
  steps: Step[]
  next_action: NextAction
  next_wave_position?: number
  completed_waves: number
}

interface CreateResponse extends PlanDetail {
  replayed: boolean
}

interface AdvanceResponse extends PlanDetail {
  advance: {
    action: NextAction
    action_label_th: string
    wave_position: number
    wave_id: string
    attempt: number
    steps_recorded: number
    completed_waves: number
    completed_waves_preserved: number
    summary_th: string
    steps: Step[]
  }
}

interface GateResponse extends PlanDetail {
  wave: Wave
  gate: GateReport
  recorded_steps: Step[]
  halted: boolean
  summary_th: string
}

interface Policy {
  version: number
  simulated_only: boolean
  max_tenants: number
  cohorts: {
    cohort: Cohort
    label_th: string
    position: number
    max_tenants: number
    gate_thresholds: Thresholds
  }[]
  gate_checks: { key: string; label_th: string; rule_th: string }[]
}

interface PlanForm {
  name: string
  release: string
  canary: string
  early: string
  general: string
}

interface CreatePayload {
  name: string
  release: string
  cohorts: {
    canary: RolloutTenant[]
    early: RolloutTenant[]
    general: RolloutTenant[]
  }
}

const PLANS_KEY = ['rollout-plans']
const POLICY_KEY = ['rollout-policy']

function defaultForm(): PlanForm {
  return {
    name: 'เปิดใช้รุ่น 2026.11.1 ให้ทุกองค์กร',
    release: '2026.11.1',
    canary: '',
    early: '',
    general: '',
  }
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function parseTenants(raw: string): RolloutTenant[] {
  const lines = raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
  return lines.map((line, index) => {
    const parts = line.split(',').map((part) => part.trim())
    if (parts.length < 2 || parts[0] === '' || parts[1] === '') {
      throw new Error(`บรรทัดที่ ${index + 1}: ต้องมี tenant_id และ slug คั่นด้วยเครื่องหมายจุลภาค`)
    }
    return {
      tenant_id: parts[0],
      slug: parts[1],
      name_th: parts[2] ?? '',
    }
  })
}

function percent(value: number): string {
  return `${(value * 100).toFixed(2)}%`
}

function cohortLabel(cohort: Cohort): string {
  switch (cohort) {
    case 'canary':
      return 'กลุ่มนำร่อง (canary)'
    case 'early':
      return 'กลุ่มแรก (early)'
    case 'general':
      return 'กลุ่มทั่วไป (general)'
  }
}

function planStatusClass(status: PlanStatus): string {
  switch (status) {
    case 'completed':
      return 'badge badge-closed'
    case 'halted':
      return 'badge badge-received'
    case 'in_progress':
      return 'badge badge-reviewing'
    case 'planned':
      return 'chip'
  }
}

function waveStatusClass(status: WaveStatus): string {
  switch (status) {
    case 'completed':
      return 'badge badge-closed'
    case 'halted':
      return 'badge badge-received'
    case 'awaiting_gate':
      return 'badge badge-reviewing'
    case 'pending':
      return 'chip'
  }
}

function stepStatusClass(status: Step['status']): string {
  if (status === 'succeeded') return 'badge badge-closed'
  if (status === 'failed') return 'badge badge-received'
  if (status === 'prepared') return 'badge badge-reviewing'
  return 'chip'
}

function stepStatusLabel(status: Step['status']): string {
  switch (status) {
    case 'succeeded':
      return 'สำเร็จ (จำลอง)'
    case 'failed':
      return 'ไม่ผ่าน'
    case 'prepared':
      return 'เตรียมไว้ ยังไม่เรียกใช้'
    case 'skipped':
      return 'ข้าม'
  }
}

function stepKindLabel(kind: Step['kind']): string {
  switch (kind) {
    case 'deploy':
      return 'ติดตั้ง'
    case 'gate':
      return 'เกต'
    case 'resume':
      return 'เริ่มใหม่'
    case 'rollback':
      return 'แผนย้อนกลับ'
  }
}

function nextActionLabel(action: NextAction): string {
  switch (action) {
    case 'deploy':
      return 'ติดตั้ง wave ถัดไป'
    case 'resume':
      return 'เริ่มใหม่จาก wave ที่หยุด'
    case 'await_gate':
      return 'รอผลเกตของ wave ปัจจุบัน'
    case 'done':
      return 'ครบทุก wave แล้ว'
  }
}

function gateCheckClass(pass: boolean): string {
  return pass ? 'badge badge-closed' : 'badge badge-received'
}

export function Rollout() {
  const qc = useQueryClient()
  const [form, setForm] = useState<PlanForm>(defaultForm)
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [selectedID, setSelectedID] = useState('')
  const [note, setNote] = useState('')
  const [gateForm, setGateForm] = useState({ requests: 600, errors: 2, p95_ms: 400 })

  const policyQ = useQuery({
    queryKey: POLICY_KEY,
    queryFn: () => api<Policy>('/v1/rollout/policy'),
    staleTime: 60_000,
  })

  const plansQ = useQuery({
    queryKey: PLANS_KEY,
    queryFn: () => api<RolloutPlan[]>('/v1/rollout/plans?limit=50'),
    staleTime: 15_000,
  })

  const detailQ = useQuery({
    queryKey: ['rollout-plan', selectedID],
    queryFn: () => api<PlanDetail>(`/v1/rollout/plans/${selectedID}`),
    enabled: selectedID !== '',
    staleTime: 10_000,
  })

  const awaitingWave = detailQ.data?.waves.find((wave) => wave.status === 'awaiting_gate')
  const awaitingPosition = awaitingWave ? awaitingWave.position : 0

  const create = useMutation({
    mutationFn: (payload: CreatePayload) =>
      apiWrite<CreateResponse>('/v1/rollout/plans', 'POST', { ...payload, simulated: true }),
    onSuccess: (result) => {
      setSelectedID(result.plan.id)
      setNotice(
        result.replayed
          ? `แผนนี้มีอยู่แล้ว · ใช้แผน ${result.plan.id} ซ้ำโดยไม่สร้าง wave ใหม่`
          : `สร้างแผน ${result.plan.name} แล้ว · ${result.plan.tenants_total} องค์กร ใน 3 wave`,
      )
      void qc.invalidateQueries({ queryKey: PLANS_KEY })
      void qc.invalidateQueries({ queryKey: ['rollout-plan', result.plan.id] })
    },
  })

  const advance = useMutation({
    mutationFn: () =>
      apiWrite<AdvanceResponse>(`/v1/rollout/plans/${selectedID}/advance`, 'POST', {
        note: note.trim(),
      }),
    onSuccess: (result) => {
      setNotice(result.advance.summary_th)
      setNote('')
      void qc.invalidateQueries({ queryKey: ['rollout-plan', selectedID] })
      void qc.invalidateQueries({ queryKey: PLANS_KEY })
    },
  })

  const recordGate = useMutation({
    mutationFn: () =>
      apiWrite<GateResponse>(`/v1/rollout/plans/${selectedID}/waves/${awaitingPosition}/gate`, 'POST', {
        requests: gateForm.requests,
        errors: gateForm.errors,
        p95_ms: gateForm.p95_ms,
      }),
    onSuccess: (result) => {
      setNotice(result.summary_th)
      void qc.invalidateQueries({ queryKey: ['rollout-plan', selectedID] })
      void qc.invalidateQueries({ queryKey: PLANS_KEY })
    },
  })

  const plans = plansQ.data ?? []
  const policy = policyQ.data
  const detail = detailQ.data
  const nextAction = detail?.next_action ?? 'await_gate'
  const advanceDisabled = advance.isPending || nextAction === 'await_gate' || nextAction === 'done'

  const head = (
    <PageHead
      title="กำกับ rollout หลายองค์กร"
      sub="ปล่อยรุ่นเดียวผ่านสาม wave ตามลำดับ canary → early → general · เกตตัดสินจากอัตราข้อผิดพลาด p95 และจำนวนตัวอย่างจริง · เกตที่ไม่ผ่านจะหยุด rollout โดยคง wave ที่เสร็จแล้วไว้ และการสั่งใหม่จะเริ่มจาก wave ที่หยุด"
      actions={
        <button
          className="btn"
          disabled={plansQ.isFetching || policyQ.isFetching}
          onClick={() => {
            void plansQ.refetch()
            void policyQ.refetch()
            if (selectedID) void detailQ.refetch()
          }}
        >
          {plansQ.isFetching || policyQ.isFetching ? 'กำลังตรวจสอบ…' : 'ตรวจสอบใหม่'}
        </button>
      }
    />
  )

  if (plansQ.isLoading || policyQ.isLoading) {
    return (
      <>
        {head}
        <div className="stack">
          <Skel h={110} />
          <Skel h={340} />
        </div>
      </>
    )
  }

  if (plansQ.isError || policyQ.isError || !policy) {
    return (
      <>
        {head}
        <Empty
          title="โหลดข้อมูล rollout ไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าไมเกรชัน 0124 ถูกนำไปใช้กับฐานข้อมูลนี้แล้ว"
        />
      </>
    )
  }

  function submitPlan() {
    try {
      const payload: CreatePayload = {
        name: form.name.trim(),
        release: form.release.trim(),
        cohorts: {
          canary: parseTenants(form.canary),
          early: parseTenants(form.early),
          general: parseTenants(form.general),
        },
      }
      if (payload.name === '' || payload.release === '') {
        throw new Error('ต้องกรอกชื่อแผนและรุ่นที่ปล่อย')
      }
      if (
        payload.cohorts.canary.length === 0 ||
        payload.cohorts.early.length === 0 ||
        payload.cohorts.general.length === 0
      ) {
        throw new Error('ทุก cohort ต้องมีอย่างน้อยหนึ่งองค์กร เพราะ wave ทั้งสามเป็นส่วนบังคับของแผน')
      }
      setFormError('')
      create.mutate(payload)
    } catch (err) {
      setFormError(errorText(err, 'ตรวจสอบข้อมูลแผนไม่สำเร็จ'))
    }
  }

  const stats = detail
    ? [
        { label: 'wave ที่เสร็จแล้ว', value: `${number(detail.completed_waves)}/3`, foot: detail.plan.status_label_th },
        { label: 'องค์กรเป้าหมาย', value: number(detail.plan.tenants_total), foot: 'กระจายตาม cohort' },
        {
          label: 'ขั้นตอนที่บันทึก',
          value: number(detail.steps.length),
          foot: 'ทุกขั้นเป็นผลจำลอง',
        },
        {
          label: 'การสั่งครั้งถัดไป',
          value: nextActionLabel(detail.next_action),
          foot: detail.plan.simulated ? 'โหมดจำลองเท่านั้น' : 'โหมดจริง',
        },
      ]
    : [
        { label: 'แผนทั้งหมด', value: number(plans.length), foot: 'เรียงจากใหม่ที่สุด' },
        { label: 'แผนที่เสร็จสิ้น', value: number(plans.filter((plan) => plan.status === 'completed').length), foot: 'ครบสาม wave' },
        { label: 'แผนที่หยุดกลางทาง', value: number(plans.filter((plan) => plan.status === 'halted').length), foot: 'รอผู้ดูแลตัดสินใจ' },
        { label: 'โหมดบันทึกผล', value: 'จำลอง', foot: 'ไม่มีคำสั่งติดตั้งจริง' },
      ]

  return (
    <>
      {head}

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        {stats.map((stat) => (
          <Stat key={stat.label} label={stat.label} value={stat.value} foot={stat.foot} />
        ))}
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="card-label">สร้างแผน rollout ใหม่</div>
        <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
          หนึ่งบรรทัดต่อหนึ่งองค์กร ในรูปแบบ <span className="mono">tenant_id, slug, ชื่อ</span> ·
          ลำดับ wave ถูกกำหนดตายตัวว่า canary → early → general และทุก cohort ต้องมีองค์กรอย่างน้อยหนึ่งราย
        </p>

        <div className="grid-2" style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="rollout-name">ชื่อแผน</label>
            <input
              id="rollout-name"
              className="input"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="rollout-release">รุ่นที่ปล่อย</label>
            <input
              id="rollout-release"
              className="input"
              value={form.release}
              onChange={(event) => setForm({ ...form, release: event.target.value })}
            />
          </div>
        </div>

        <div className="grid-3" style={{ marginTop: 8 }}>
          <div className="field">
            <label htmlFor="rollout-canary">canary · สูงสุด {number(policy.cohorts[0]?.max_tenants ?? 10)} องค์กร</label>
            <textarea
              id="rollout-canary"
              className="input mono"
              rows={4}
              placeholder="uuid ขององค์กร, slug, ชื่อ"
              value={form.canary}
              onChange={(event) => setForm({ ...form, canary: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="rollout-early">early · สูงสุด {number(policy.cohorts[1]?.max_tenants ?? 100)} องค์กร</label>
            <textarea
              id="rollout-early"
              className="input mono"
              rows={4}
              placeholder="uuid ขององค์กร, slug, ชื่อ"
              value={form.early}
              onChange={(event) => setForm({ ...form, early: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="rollout-general">general · สูงสุด {number(policy.cohorts[2]?.max_tenants ?? 500)} องค์กร</label>
            <textarea
              id="rollout-general"
              className="input mono"
              rows={4}
              placeholder="uuid ขององค์กร, slug, ชื่อ"
              value={form.general}
              onChange={(event) => setForm({ ...form, general: event.target.value })}
            />
          </div>
        </div>

        <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 14 }}>
          <button className="btn btn-primary" disabled={create.isPending} onClick={submitPlan}>
            {create.isPending ? 'กำลังสร้างแผน…' : 'สร้างแผน'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            คำขอต้องมี Idempotency-Key · แผนเดิมที่เนื้อหาเหมือนกันจะถูกใช้ซ้ำ ไม่สร้าง wave ซ้ำ
          </span>
        </div>
        {formError ? <div className="form-error">{formError}</div> : null}
        {create.isError ? <div className="form-error">{errorText(create.error, 'สร้างแผนไม่สำเร็จ')}</div> : null}
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="card-label">แผนที่บันทึกไว้</div>
          <span className="dim" style={{ fontSize: 11.5 }}>แสดงล่าสุด {number(plans.length)} แผน</span>
        </div>
        <div className="table-wrap" style={{ marginTop: 14 }}>
          {plans.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty title="ยังไม่มีแผน rollout" body="สร้างแผนด้านบนเพื่อกำหนด wave และเกตก่อนปล่อยรุ่นแรก" />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>สร้างเมื่อ</th>
                  <th>ชื่อแผน</th>
                  <th style={{ width: 120 }}>รุ่น</th>
                  <th style={{ width: 130 }}>สถานะ</th>
                  <th style={{ width: 110 }}>wave</th>
                  <th style={{ width: 100 }}>องค์กร</th>
                  <th style={{ width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr
                    key={plan.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedID(plan.id)
                      setNotice(null)
                    }}
                  >
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(plan.created_at)}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {plan.name}
                      <div className="dim mono" style={{ fontSize: 10, marginTop: 3 }}>{plan.plan_key.slice(0, 16)}…</div>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{plan.release}</td>
                    <td><span className={planStatusClass(plan.status)}>{plan.status_label_th}</span></td>
                    <td style={{ fontSize: 11.5 }}>{number(plan.completed_waves)}/3 เสร็จ</td>
                    <td style={{ fontSize: 11.5 }}>{number(plan.tenants_total)}</td>
                    <td>
                      <button className="btn" style={{ height: 28, padding: '0 10px', fontSize: 11 }}>
                        ดู
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detailQ.isLoading ? <Skel h={300} /> : null}
      {detailQ.isError ? (
        <Empty title="โหลดรายละเอียดแผนไม่ได้" body="ตรวจสอบรหัสแผนและการเชื่อมต่อกับเซิร์ฟเวอร์" />
      ) : null}

      {detail ? (
        <>
          <div className="card card-pad" style={{ marginBottom: 22 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
              <div>
                <div className="card-label">แผนที่เลือก</div>
                <div style={{ fontSize: 15, marginTop: 6 }}>{detail.plan.name}</div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                  รุ่น <span className="mono">{detail.plan.release}</span> · สร้าง {thaiDateTime(detail.plan.created_at)} ·
                  อัปเดต {thaiDateTime(detail.plan.updated_at)} · {number(detail.plan.tenants_total)} องค์กร
                </div>
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className={planStatusClass(detail.plan.status)}>{detail.plan.status_label_th}</span>
                <span className="chip">{detail.plan.simulated ? 'ผลจำลอง' : 'ผลจริง'}</span>
              </div>
            </div>

            <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: 1, minWidth: 260 }}>
                <label htmlFor="rollout-note">หมายเหตุการสั่ง (ไม่บังคับ)</label>
                <input
                  id="rollout-note"
                  className="input"
                  placeholder="เช่น ตรวจสอบสาเหตุของข้อผิดพลาดแล้ว"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
              <button
                className="btn btn-primary"
                disabled={advanceDisabled}
                title={nextAction === 'await_gate' ? 'ต้องบันทึกผลเกตของ wave ปัจจุบันก่อน' : undefined}
                onClick={() => advance.mutate()}
              >
                {advance.isPending ? 'กำลังดำเนินการ…' : nextActionLabel(detail.next_action)}
              </button>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              {detail.next_action === 'await_gate'
                ? 'wave ปัจจุบันติดตั้งแล้วและรอผลเกต · การสั่งเลื่อนจะถูกปฏิเสธจนกว่าจะบันทึกผล'
                : detail.next_action === 'done'
                  ? 'ทุก wave เสร็จสิ้นแล้ว ไม่มีคำสั่งถัดไป'
                  : detail.next_action === 'resume'
                    ? 'แผนหยุดอยู่ที่ wave ก่อนหน้า · การสั่งจะเริ่มจาก wave ที่หยุดและไม่ติดตั้ง wave ที่เสร็จแล้วซ้ำ'
                    : 'การสั่งจะติดตั้ง wave ถัดไปและบันทึกผลขั้นตอนแบบจำลอง'}
            </div>
            {advance.isError ? <div className="form-error">{errorText(advance.error, 'ดำเนินการไม่สำเร็จ')}</div> : null}
          </div>

          <div className="stack" style={{ marginBottom: 22 }}>
            {detail.waves.map((wave) => (
              <div className="card card-pad" key={wave.id}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div className="card-label">wave {wave.position + 1} · {cohortLabel(wave.cohort)}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                      {number(wave.tenant_count)} องค์กร · พยายาม {number(wave.attempt)} ครั้ง
                      {wave.deployed_at ? ` · ติดตั้ง ${thaiDateTime(wave.deployed_at)}` : ''}
                      {wave.completed_at ? ` · เสร็จ ${thaiDateTime(wave.completed_at)}` : ''}
                      {wave.halted_at ? ` · หยุดครั้งก่อน ${thaiDateTime(wave.halted_at)}` : ''}
                    </div>
                  </div>
                  <span className={waveStatusClass(wave.status)}>{wave.status_label_th}</span>
                </div>

                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  <span className="chip">ตัวอย่างขั้นต่ำ {number(wave.gate_thresholds.min_requests)} คำขอ</span>
                  <span className="chip">error rate ≤ {percent(wave.gate_thresholds.max_error_rate)}</span>
                  <span className="chip">p95 ≤ {number(wave.gate_thresholds.max_p95_ms)} ms</span>
                </div>

                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {wave.tenants.map((tenant) => (
                    <span className="chip" key={tenant.tenant_id} title={tenant.tenant_id}>
                      {tenant.name_th || tenant.slug}
                    </span>
                  ))}
                </div>

                {wave.failure_th ? (
                  <div className="card card-pad" style={{ marginTop: 14, background: 'transparent' }}>
                    <div className="card-label">เหตุผลที่หยุด</div>
                    <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 6 }}>{wave.failure_th}</div>
                  </div>
                ) : null}

                {wave.last_gate ? (
                  <>
                    <div className="row" style={{ justifyContent: 'space-between', gap: 10, marginTop: 16 }}>
                      <div className="card-label">ผลเกตล่าสุด</div>
                      <span className={wave.last_gate.passed ? 'badge badge-closed' : 'badge badge-received'}>
                        {wave.last_gate.decision_th}
                      </span>
                    </div>
                    <div className="dim" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.8 }}>{wave.last_gate.summary_th}</div>
                    <div className="table-wrap" style={{ marginTop: 10 }}>
                      <table className="table data">
                        <thead>
                          <tr>
                            <th style={{ width: 170 }}>รายการ</th>
                            <th style={{ width: 170 }}>ค่าที่วัดได้</th>
                            <th style={{ width: 150 }}>เกณฑ์</th>
                            <th style={{ width: 90 }}>ผล</th>
                            <th>หลักฐาน</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wave.last_gate.checks.map((check) => (
                            <tr key={check.key} style={{ cursor: 'default' }}>
                              <td style={{ fontSize: 12 }}>{check.label_th}</td>
                              <td className="mono" style={{ fontSize: 11 }}>{check.observed}</td>
                              <td className="mono" style={{ fontSize: 11 }}>{check.limit}</td>
                              <td><span className={gateCheckClass(check.pass)}>{check.pass ? 'ผ่าน' : 'ไม่ผ่าน'}</span></td>
                              <td className="dim" style={{ fontSize: 11.5 }}>{check.detail_th}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}

                <details style={{ marginTop: 14 }}>
                  <summary className="dim" style={{ fontSize: 12, cursor: 'pointer' }}>
                    แผนติดตั้ง {number(wave.deploy_plan.length)} ขั้น · แผนย้อนกลับ {number(wave.rollback_plan.length)} ขั้น
                  </summary>
                  <div className="grid-2" style={{ marginTop: 10 }}>
                    <div>
                      <div className="card-label">ลำดับติดตั้ง</div>
                      <ol className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, paddingLeft: 18 }}>
                        {wave.deploy_plan.map((step) => (
                          <li key={step.id}>{step.title_th} · ประมาณ {number(step.estimated_seconds)} วินาที</li>
                        ))}
                      </ol>
                    </div>
                    <div>
                      <div className="card-label">แผนย้อนกลับ</div>
                      <ol className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, paddingLeft: 18 }}>
                        {wave.rollback_plan.map((step) => (
                          <li key={step.id}>
                            {step.title_th}
                            {step.requires_approval ? ' · ต้องอนุมัติ' : ''}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </details>
              </div>
            ))}
          </div>

          {awaitingWave ? (
            <div className="card card-pad" style={{ marginBottom: 22 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div className="card-label">บันทึกผลเกตของ wave {awaitingWave.position + 1}</div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                    กรอกค่าที่วัดได้จริงในช่วงเวลาที่ wave นี้รับทราฟฟิก · จำนวนตัวอย่างต้องถึงขั้นต่ำ {number(awaitingWave.gate_thresholds.min_requests)} คำขอ
                  </div>
                </div>
                <span className="chip">{cohortLabel(awaitingWave.cohort)}</span>
              </div>

              <div className="grid-3" style={{ marginTop: 14 }}>
                <div className="field">
                  <label htmlFor="rollout-requests">จำนวนคำขอ</label>
                  <input
                    id="rollout-requests"
                    className="input"
                    type="number"
                    min={0}
                    value={gateForm.requests}
                    onChange={(event) => setGateForm({ ...gateForm, requests: Number(event.target.value) })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="rollout-errors">จำนวนข้อผิดพลาด</label>
                  <input
                    id="rollout-errors"
                    className="input"
                    type="number"
                    min={0}
                    value={gateForm.errors}
                    onChange={(event) => setGateForm({ ...gateForm, errors: Number(event.target.value) })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="rollout-p95">p95 (ms)</label>
                  <input
                    id="rollout-p95"
                    className="input"
                    type="number"
                    min={0}
                    value={gateForm.p95_ms}
                    onChange={(event) => setGateForm({ ...gateForm, p95_ms: Number(event.target.value) })}
                  />
                </div>
              </div>

              <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-primary" disabled={recordGate.isPending} onClick={() => recordGate.mutate()}>
                  {recordGate.isPending ? 'กำลังประเมิน…' : 'บันทึกผลเกต'}
                </button>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  เกตไม่ผ่านจะหยุด rollout ทันที และบันทึกแผนย้อนกลับไว้โดยไม่เรียกใช้
                </span>
              </div>
              {recordGate.isError ? <div className="form-error">{errorText(recordGate.error, 'บันทึกผลเกตไม่สำเร็จ')}</div> : null}
            </div>
          ) : null}

          <div className="card card-pad" style={{ marginBottom: 22 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div className="card-label">บันทึกขั้นตอนแบบจำลอง</div>
              <span className="dim" style={{ fontSize: 11.5 }}>
                {number(detail.steps.filter((step) => step.simulated).length)}/{number(detail.steps.length)} รายการเป็นผลจำลอง
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              {detail.steps.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <Empty title="ยังไม่มีขั้นตอน" body="กดติดตั้ง wave แรกเพื่อบันทึกผลขั้นตอนแบบจำลอง" />
                </div>
              ) : (
                <table className="table data">
                  <thead>
                    <tr>
                      <th style={{ width: 46 }}>#</th>
                      <th style={{ width: 120 }}>ประเภท</th>
                      <th style={{ width: 220 }}>ขั้นตอน</th>
                      <th style={{ width: 150 }}>สถานะ</th>
                      <th>หลักฐาน</th>
                      <th style={{ width: 120 }}>เวลา</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.steps.map((step) => (
                      <tr key={step.id} style={{ cursor: 'default' }}>
                        <td className="mono">{step.position}</td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{stepKindLabel(step.kind)}</td>
                        <td style={{ fontSize: 12 }}>
                          {step.title_th}
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                            ความพยายาม {number(step.attempt)} · {step.step_key}
                          </div>
                        </td>
                        <td>
                          <span className={stepStatusClass(step.status)}>{stepStatusLabel(step.status)}</span>
                          <div className="dim mono" style={{ fontSize: 10, marginTop: 3 }}>
                            simulated={step.simulated ? 'true' : 'false'}
                          </div>
                        </td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{step.evidence_th}</td>
                        <td className="dim" style={{ fontSize: 10.5 }}>{thaiDateTime(step.recorded_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : null}

      <div className="card card-pad">
        <div className="card-label">นโยบายเกตของแต่ละ wave</div>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table data">
            <thead>
              <tr>
                <th style={{ width: 200 }}>wave</th>
                <th style={{ width: 110 }}>ตำแหน่ง</th>
                <th style={{ width: 130 }}>องค์กรสูงสุด</th>
                <th style={{ width: 150 }}>ตัวอย่างขั้นต่ำ</th>
                <th style={{ width: 140 }}>error rate</th>
                <th>p95</th>
              </tr>
            </thead>
            <tbody>
              {policy.cohorts.map((cohort) => (
                <tr key={cohort.cohort} style={{ cursor: 'default' }}>
                  <td style={{ fontSize: 12.5 }}>{cohort.label_th}</td>
                  <td className="mono">{cohort.position + 1}</td>
                  <td>{number(cohort.max_tenants)}</td>
                  <td>{number(cohort.gate_thresholds.min_requests)} คำขอ</td>
                  <td>{percent(cohort.gate_thresholds.max_error_rate)}</td>
                  <td>{number(cohort.gate_thresholds.max_p95_ms)} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.8 }}>
          {policy.gate_checks.map((check) => `${check.label_th}: ${check.rule_th}`).join(' · ')}
        </div>
      </div>
    </>
  )
}
